import React, { useState, useRef } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, Legend, ResponsiveContainer, Tooltip
} from 'recharts';
import { Maximize2, Printer, X, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COLORS = [
  '#002FA7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
];

const MODALITIES = ['Listening', 'Speaking', 'Reading', 'Writing'];

const buildChartData = (series, overlay, targets) =>
  MODALITIES.map(mod => {
    const key = mod.toLowerCase();
    const point = { modality: mod };
    series.forEach((s, i) => { point[`series_${i}`] = s[key] || 0; });
    if (overlay) point['overlay'] = overlay[key] || 0;
    if (targets) point['target'] = targets[key] || 0;
    return point;
  });

const RadarChartContent = ({ chartData, series, targets, overlay, chartHeight, fontSize = 'normal' }) => {
  const tickSize = fontSize === 'large' ? 16 : 12;
  const radiusSize = fontSize === 'large' ? 11 : 9;
  const dotR = fontSize === 'large' ? 4 : 3;
  const strokeW = fontSize === 'large' ? 3 : 2;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="75%">
        <PolarGrid stroke="#E5E7EB" />
        <PolarAngleAxis dataKey="modality" tick={{ fontSize: tickSize, fontWeight: 600, fill: '#374151' }} />
        <PolarRadiusAxis angle={90} domain={[0, 6]} tickCount={7} tick={{ fontSize: radiusSize, fill: '#9CA3AF' }} />

        {targets && (
          <Radar name="District Target" dataKey="target" stroke="#DC2626" fill="transparent"
            strokeWidth={strokeW} strokeDasharray="6 3" dot={false} />
        )}
        {overlay && (
          <Radar name={overlay.name || 'Average'} dataKey="overlay" stroke="#9CA3AF" fill="#9CA3AF"
            fillOpacity={0.08} strokeWidth={strokeW - 0.5} strokeDasharray="4 2" />
        )}
        {series.map((s, i) => (
          <Radar key={s.name || i} name={s.name || `Series ${i + 1}`} dataKey={`series_${i}`}
            stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]}
            fillOpacity={series.length === 1 ? 0.2 : 0.08} strokeWidth={strokeW}
            dot={{ r: dotR, fill: COLORS[i % COLORS.length] }} />
        ))}
        <Tooltip contentStyle={{ background: 'white', border: '2px solid #E5E7EB', borderRadius: 0, fontSize: 12 }}
          formatter={(value) => [value.toFixed(1), '']} />
        {(series.length > 1 || targets || overlay) && (
          <Legend wrapperStyle={{ fontSize: fontSize === 'large' ? 14 : 11, paddingTop: 8 }} iconType="line" />
        )}
      </RadarChart>
    </ResponsiveContainer>
  );
};

const ScoreTable = ({ series, targets, overlay }) => (
  <table className="w-full text-sm border-collapse mt-4" data-testid="spider-score-table">
    <thead>
      <tr className="bg-gray-50">
        <th className="text-left p-2 border border-gray-200 font-bold text-xs uppercase text-gray-500">Name</th>
        {MODALITIES.map(m => (
          <th key={m} className="text-center p-2 border border-gray-200 font-bold text-xs uppercase text-gray-500">{m}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {series.map((s, i) => (
        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
          <td className="p-2 border border-gray-200 font-semibold">
            <span className="inline-block w-3 h-3 mr-2" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
            {s.name}
          </td>
          {MODALITIES.map(m => {
            const val = s[m.toLowerCase()] || 0;
            const target = targets?.[m.toLowerCase()];
            const belowTarget = target && val < target;
            return (
              <td key={m} className={`text-center p-2 border border-gray-200 font-mono ${belowTarget ? 'text-red-600 font-bold' : ''}`}>
                {val.toFixed(1)}
              </td>
            );
          })}
        </tr>
      ))}
      {overlay && (
        <tr className="bg-gray-100">
          <td className="p-2 border border-gray-200 text-gray-500 italic">{overlay.name || 'Average'}</td>
          {MODALITIES.map(m => (
            <td key={m} className="text-center p-2 border border-gray-200 text-gray-500 italic font-mono">
              {(overlay[m.toLowerCase()] || 0).toFixed(1)}
            </td>
          ))}
        </tr>
      )}
      {targets && (
        <tr className="bg-red-50">
          <td className="p-2 border border-gray-200 text-red-600 font-semibold">District Target</td>
          {MODALITIES.map(m => (
            <td key={m} className="text-center p-2 border border-gray-200 text-red-600 font-bold font-mono">
              {(targets[m.toLowerCase()] || 0).toFixed(1)}
            </td>
          ))}
        </tr>
      )}
    </tbody>
  </table>
);

/**
 * ProficiencySpiderChart — Reusable radar chart with expand and print.
 */
const ProficiencySpiderChart = ({ series = [], targets, overlay, height = 320, title, subtitle }) => {
  const [expanded, setExpanded] = useState(false);
  const printRef = useRef(null);

  if (!series.length && !overlay) {
    return (
      <div className="border-2 border-gray-200 bg-white p-6 text-center text-gray-400">
        <p className="text-sm">No assessment data available yet</p>
      </div>
    );
  }

  const chartData = buildChartData(series, overlay, targets);
  const hasData = series.some(s => s.listening || s.speaking || s.reading || s.writing);
  if (!hasData && !overlay) {
    return (
      <div className="border-2 border-gray-200 bg-white p-6 text-center text-gray-600">
        <p className="text-sm">No scores recorded yet</p>
      </div>
    );
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const seriesRows = series.map((s, i) =>
      `<tr style="background:${i % 2 === 0 ? '#fff' : '#F9FAFB'}">
        <td style="padding:8px;border:1px solid #E5E7EB;font-weight:600">
          <span style="display:inline-block;width:12px;height:12px;background:${COLORS[i % COLORS.length]};margin-right:8px;vertical-align:middle"></span>
          ${s.name}
        </td>
        ${MODALITIES.map(m => {
          const val = (s[m.toLowerCase()] || 0).toFixed(1);
          const t = targets?.[m.toLowerCase()];
          const below = t && parseFloat(val) < t;
          return `<td style="text-align:center;padding:8px;border:1px solid #E5E7EB;font-family:monospace;${below ? 'color:#DC2626;font-weight:bold' : ''}">${val}</td>`;
        }).join('')}
      </tr>`
    ).join('');

    const overlayRow = overlay ? `<tr style="background:#F3F4F6">
      <td style="padding:8px;border:1px solid #E5E7EB;color:#6B7280;font-style:italic">${overlay.name || 'Average'}</td>
      ${MODALITIES.map(m => `<td style="text-align:center;padding:8px;border:1px solid #E5E7EB;color:#6B7280;font-style:italic;font-family:monospace">${(overlay[m.toLowerCase()] || 0).toFixed(1)}</td>`).join('')}
    </tr>` : '';

    const targetRow = targets ? `<tr style="background:#FEF2F2">
      <td style="padding:8px;border:1px solid #E5E7EB;color:#DC2626;font-weight:600">District Target</td>
      ${MODALITIES.map(m => `<td style="text-align:center;padding:8px;border:1px solid #E5E7EB;color:#DC2626;font-weight:bold;font-family:monospace">${(targets[m.toLowerCase()] || 0).toFixed(1)}</td>`).join('')}
    </tr>` : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title || 'WIDA Spider Chart'} — MOSAIC</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #111827; }
          h1 { font-size: 22px; margin: 0; color: #002FA7; }
          h2 { font-size: 14px; margin: 4px 0 24px; color: #6B7280; font-weight: normal; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #F3F4F6; padding: 8px; border: 1px solid #E5E7EB; font-size: 11px; text-transform: uppercase; color: #6B7280; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #002FA7; padding-bottom: 12px; margin-bottom: 20px; }
          .logo { font-size: 18px; font-weight: bold; color: #002FA7; }
          .date { font-size: 12px; color: #9CA3AF; }
          .footer { margin-top: 40px; font-size: 10px; color: #9CA3AF; text-align: center; border-top: 1px solid #E5E7EB; padding-top: 12px; }
          .note { font-size: 11px; color: #6B7280; margin-top: 12px; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">MOSAIC</div>
            <div style="font-size:11px;color:#6B7280">Mosaic Language Proficiency Assessment</div>
          </div>
          <div class="date">Printed: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
        <h1>${title || 'WIDA Modality Performance'}</h1>
        <h2>${subtitle || ''}</h2>
        <p class="note">Scores on a 1–6 WIDA scale across Listening, Speaking, Reading, and Writing.${targets ? ' <span style="color:#DC2626">Red values</span> are below district target.' : ''}</p>
        <table>
          <thead>
            <tr>
              <th style="text-align:left">Name</th>
              ${MODALITIES.map(m => `<th style="text-align:center">${m}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${seriesRows}
            ${overlayRow}
            ${targetRow}
          </tbody>
        </table>
        <div class="footer">
          MOSAIC by Lerno Co. | mosaicassessmentco.com<br/>
          WIDA&reg; is a registered trademark of the Board of Regents of the University of Wisconsin System.
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  // Fullscreen modal
  if (expanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setExpanded(false)} data-testid="spider-modal">
        <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-5 border-b-2 border-gray-200">
            <div>
              <h2 className="font-bold text-lg text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
              {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handlePrint} className="text-xs h-8" style={{ borderRadius: 0 }} data-testid="spider-print-btn">
                <Printer size={14} className="mr-1" /> Print Report
              </Button>
              <Button size="sm" variant="outline" onClick={() => setExpanded(false)} className="text-xs h-8" style={{ borderRadius: 0 }} data-testid="spider-close-btn">
                <Minimize2 size={14} className="mr-1" /> Close
              </Button>
            </div>
          </div>
          <div className="p-5" ref={printRef}>
            <RadarChartContent chartData={chartData} series={series} targets={targets} overlay={overlay} chartHeight={500} fontSize="large" />
            <ScoreTable series={series} targets={targets} overlay={overlay} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-gray-200 bg-white p-4" data-testid="wida-spider-chart">
      {title && (
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-sm text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => setExpanded(true)} className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="Expand" data-testid="spider-expand-btn">
              <Maximize2 size={15} />
            </button>
            <button onClick={handlePrint} className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="Print" data-testid="spider-print-inline-btn">
              <Printer size={15} />
            </button>
          </div>
        </div>
      )}
      <RadarChartContent chartData={chartData} series={series} targets={targets} overlay={overlay} chartHeight={height} />
    </div>
  );
};

export default ProficiencySpiderChart;
